import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCalendar, FiClock, FiUser, FiCheck, FiX, FiInfo, FiMoreVertical, FiInbox } = FiIcons;

const AppointmentQueue = () => {
  const [appointments, setAppointments] = useState([
    { id: 1, user: 'Emily Watson', resource: 'Grief Counselor', date: '2024-05-20', time: '10:30 AM', status: 'pending' },
    { id: 2, user: 'James Miller', resource: 'Estate Attorney', date: '2024-05-21', time: '02:00 PM', status: 'pending' },
    { id: 3, user: 'Sarah Chen', resource: 'Funeral Director', date: '2024-05-22', time: '09:00 AM', status: 'pending' },
    { id: 4, user: 'Robert Wilson', resource: 'Grief Counselor', date: '2024-05-20', time: '04:00 PM', status: 'approved' },
  ]);

  const handleStatusUpdate = (id, newStatus) => {
    setAppointments(prev => 
      prev.map(app => app.id === id ? { ...app, status: newStatus } : app)
    );
  };

  const pendingCount = appointments.filter(a => a.status === 'pending').length;

  return (
    <div className="space-y-8">
      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-2">Pending Requests</p>
          <h4 className="text-4xl font-serif text-accent-navy">{pendingCount}</h4>
        </div>
        <div className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-2">Approved Today</p>
          <h4 className="text-4xl font-serif text-accent-navy">12</h4>
        </div>
        <div className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-2">Completion Rate</p>
          <h4 className="text-4xl font-serif text-accent-navy text-green-600">94%</h4>
        </div>
      </div>

      {/* Main Queue */}
      <div className="bg-white rounded-[40px] border border-brand-dark shadow-sm overflow-hidden">
        <div className="p-8 border-b border-brand-light flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-serif text-accent-navy">Incoming Appointments</h3>
            <p className="text-xs text-text-muted mt-1">Review and manage professional service bookings</p>
          </div>
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-brand-light rounded-xl text-[10px] font-black uppercase tracking-widest border border-brand-dark">Export CSV</button>
          </div>
        </div>

        {appointments.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-16 h-16 bg-brand-light rounded-2xl flex items-center justify-center mx-auto mb-4 border border-brand-dark">
              <SafeIcon icon={FiInbox} className="w-8 h-8 text-text-muted/30" />
            </div>
            <p className="text-sm font-serif text-accent-navy mb-1">No Appointments</p>
            <p className="text-xs text-text-muted font-light">
              Incoming appointment requests will appear here for review.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-brand-light/30 border-b border-brand-light">
                  <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-text-muted">Client</th>
                  <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-text-muted">Service Type</th>
                  <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-text-muted">Requested Slot</th>
                  <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-text-muted">Status</th>
                  <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-text-muted text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {appointments.map((app) => (
                    <motion.tr 
                      key={app.id} 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="border-b border-brand-light group hover:bg-brand-light/20 transition-colors"
                    >
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-brand-light rounded-full flex items-center justify-center border border-brand-dark text-accent-navy font-bold text-xs">
                            {app.user.charAt(0)}
                          </div>
                          <span className="font-bold text-accent-navy text-sm">{app.user}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className="text-xs text-text-muted font-medium">{app.resource}</span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-accent-navy">{app.date}</span>
                          <span className="text-[10px] text-text-muted">{app.time}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                          app.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                          app.status === 'approved' ? 'bg-green-100 text-green-700' : 
                          'bg-red-100 text-red-700'
                        }`}>
                          {app.status}
                        </span>
                      </td>
                      <td className="px-8 py-6 text-right">
                        {app.status === 'pending' ? (
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => handleStatusUpdate(app.id, 'approved')}
                              className="p-2 hover:bg-green-50 text-green-600 rounded-lg border border-green-200"
                            >
                              <SafeIcon icon={FiCheck} />
                            </button>
                            <button 
                              onClick={() => handleStatusUpdate(app.id, 'denied')}
                              className="p-2 hover:bg-red-50 text-red-600 rounded-lg border border-red-200"
                            >
                              <SafeIcon icon={FiX} />
                            </button>
                          </div>
                        ) : (
                          <button className="p-2 text-text-muted hover:bg-brand-light rounded-lg">
                            <SafeIcon icon={FiMoreVertical} />
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentQueue;